import { Injectable } from '@angular/core';
import { Paginate } from '@shared/entities/paginate';
import { Request } from '@shared/entities/request';
import { ApiService, PER_PAGE, Pagination } from '@shared/services';
import { BehaviorSubject, Observable, Subject, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][RequestInfo] ';

export interface RequestFilterURL {
  caseNumber?: string;
  serialNumber?: string;
  status?: string;
  reason?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root'
})
export class RequestInfoService extends ApiService<Request> {
  private refresh$: Subject<Pagination>;
  all$: Observable<Paginate<Request>>;
  currentPaginator: Pagination;
  currentFilters: RequestFilterURL = {};

  set filters(value: RequestFilterURL) {
    this.currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor() {
    const masterUrl = '/info-request';
    const detailUrl = '/info-request';

    super(Request, masterUrl, detailUrl);

    this.currentPaginator = { page: 1, perPage: PER_PAGE };

    this.refresh$ = new BehaviorSubject<Pagination>(this.currentPaginator);

    this.all$ = this.refresh$.pipe(
      switchMap((pagination) => {
        if (this.currentFilters.caseNumber) {
          this.setUrlParameter('caseNumber', this.currentFilters.caseNumber);
        }
        if (this.currentFilters.serialNumber) {
          this.setUrlParameter('serialNumber', this.currentFilters.serialNumber);
        }
        if (this.currentFilters.reason) {
          this.setUrlParameter('reason', this.currentFilters.reason);
        }
        this.setUrlParameter('order_by', this.currentFilters.orderBy || 'createdDate');
        // Always set: the backend defaults to ASC (oldest first) when direction is omitted,
        // so leaving this out whenever no custom order was picked showed the newest request
        // at the bottom of the list instead of the top.
        this.setUrlParameter('direction', this.currentFilters.direction || 'DESC');
        return this.paginate(Request, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ error', err)
      })
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh request-info service');
    this.currentPaginator = { page, perPage };
    this.refresh$.next(this.currentPaginator);
    return this.all$.pipe(take(1));
  }
}
